import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";

type KanbanColumn = {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  color: string;
  displayOrder: number;
  createdAt: Date | string;
  updatedAt: Date | string;
};

const presetColors = [
  { name: "Red", value: "#ef4444" },
  { name: "Orange", value: "#f59e0b" },
  { name: "Green", value: "#10b981" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Purple", value: "#8b5cf6" },
  { name: "Pink", value: "#ec4899" },
  { name: "Gray", value: "#6b7280" },
];

const columnFormSchema = z.object({
  title: z.string().min(1, "Title is required").max(50, "Title must be less than 50 characters"),
  color: z.string().regex(/^#[0-9A-F]{6}$/i, "Invalid color format"),
});

type ColumnFormData = z.infer<typeof columnFormSchema>;

interface KanbanColumnDialogProps {
  isOpen: boolean;
  onClose: () => void;
  column?: KanbanColumn;
  onSave: (data: { title: string; color: string }) => void;
}

export default function KanbanColumnDialog({ isOpen, onClose, column, onSave }: KanbanColumnDialogProps) {
  const form = useForm<ColumnFormData>({
    resolver: zodResolver(columnFormSchema),
    defaultValues: {
      title: column?.title || "",
      color: column?.color || "#3b82f6",
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        title: column?.title || "",
        color: column?.color || "#3b82f6",
      });
    }
  }, [isOpen, column, form]);

  const handleSubmit = (data: ColumnFormData) => {
    onSave(data);
    form.reset();
    onClose();
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{column ? "Edit Column" : "Create Column"}</DialogTitle>
          <DialogDescription>
            {column ? "Update the column title and color." : "Create a new column for organizing your tasks."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., To Do, In Progress, Done"
                      {...field}
                      data-testid="input-column-title"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2" data-testid="color-picker">
                        {presetColors.map((preset) => (
                          <button
                            key={preset.value}
                            type="button"
                            onClick={() => field.onChange(preset.value)}
                            className={`w-10 h-10 rounded-md border-2 transition-all hover:scale-110 ${
                              field.value === preset.value
                                ? "border-foreground ring-2 ring-offset-2 ring-foreground"
                                : "border-border"
                            }`}
                            style={{ backgroundColor: preset.value }}
                            title={preset.name}
                            data-testid={`color-preset-${preset.name.toLowerCase()}`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="color"
                          {...field}
                          className="w-20 h-10 cursor-pointer"
                          data-testid="input-color-custom"
                        />
                        <span className="text-sm text-muted-foreground">Custom color</span>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                data-testid="button-cancel-column"
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="button-save-column">
                {column ? "Save Changes" : "Create Column"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
